import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Select, Button, message } from "antd";
import { ExpandOutlined } from "@ant-design/icons";

const { TextArea } = Input;

const urgencyOptions = [
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
];

const displayOptions = [
  { value: "NOTIFICATION", label: "Notification" },
  { value: "ALERT", label: "Alert" },
];

const ACTION_TYPE_OPTIONS = [
  {
    label: "Core Olitask Domains",
    options: [
      { value: "TASK", label: "Task" },
      { value: "TRAVO_TASK", label: "Travo Task" },
      { value: "CHAT", label: "Chat" },
      { value: "REVIEW", label: "Review" },
    ],
  },
  {
    label: "Payments",
    options: [
      { value: "PAYMENT", label: "Payment" },
      { value: "PAYMENT_METHOD", label: "Payment Method" },
      { value: "STRIPE_CONNECT_ACCOUNT", label: "Stripe Connect Account" },
      { value: "PAYOUT", label: "Payout" },
    ],
  },
  {
    label: "Profile / Identity",
    options: [
      { value: "USER_PROFILE", label: "User Profile" },
      { value: "HELPER_PROFILE", label: "Helper Profile" },
      { value: "IDENTITY_VERIFICATION", label: "Identity Verification" },
      { value: "WORK_AUTHORIZATION", label: "Work Authorization" },
    ],
  },
  {
    label: "Business / Monetization",
    options: [
      { value: "DONATION", label: "Donation" },
      { value: "PROMOTION", label: "Promotion" },
      { value: "SUBSCRIPTION", label: "Subscription" },
    ],
  },
  {
    label: "Generic / System",
    options: [
      { value: "ENTITY", label: "Entity" },
      { value: "SYSTEM", label: "System" },
    ],
  },
];

const oliveebAppOptions = [
  { value: "OLITASK_MOBILE_UI", label: "Olitask Mobile UI" },
  { value: "OLITASK_WEB_UI", label: "Olitask Web UI" },
  { value: "OLREADIA_WEB_UI", label: "Olreadia Web UI" },
  { value: "OLREADIA_MOBILE_UI", label: "Olreadia Mobile UI" },
  { value: "OVACUS_WEB_UI", label: "Ovacus Web UI" },
  { value: "OVACUS_MOBILE_UI", label: "Ovacus Mobile UI" },
  { value: "ODDWIZE_WEB_UI", label: "Oddwize Web UI" },
  { value: "ODDWIZE_MOBILE_UI", label: "Oddwize Mobile UI" },
  { value: "OVIZIA_WEB_UI", label: "Ovizia Web UI" },
  { value: "OVIZIA_MOBILE_UI", label: "Ovizia Mobile UI" },
  { value: "OLITASK_MAIN_SERVICE", label: "Olitask Main Service" },
  { value: "OLIVEEB_GATEWAY_SERVICE", label: "Oliveeb Gateway Service" },
  { value: "OLIVEEB_IDENTITY_SERVICE", label: "Oliveeb Identity Service" },
  { value: "OLIVEEB_PROFILE_SERVICE", label: "Oliveeb Profile Service" },
  { value: "OLIVEEB_PAYMENT_SERVICE", label: "Oliveeb Payment Service" },
  { value: "OLIVEEB_STORAGE_SERVICE", label: "Oliveeb Storage Service" },
  {
    value: "OLIVEEB_NOTIFICATION_SERVICE",
    label: "Oliveeb Notification Service",
  },
  { value: "OLIVEEB_CHAT_SERVICE", label: "Oliveeb Chat Service" },
  {
    value: "OLIVEEB_COMMUNICATION_SERVICE",
    label: "Oliveeb Communication Service",
  },
  { value: "OLIVEEB_EMAIL_SERVICE", label: "Oliveeb Email Service" },
  { value: "OLIVEEB_CODE_SERVICE", label: "Oliveeb Code Service" },
  { value: "OLIVEEB_CACHE_SERVICE", label: "Oliveeb Cache Service" },
  { value: "OLIVEEB_SOCIAL_SERVICE", label: "Oliveeb Social Service" },
  { value: "OLIVEEB_PM_SERVICE", label: "Oliveeb PM Service" },
  { value: "OLIVEEB_AI_SERVICE", label: "Oliveeb AI Service" },
  { value: "OLIVEEB_OLREADIA_SERVICE", label: "Oliveeb Olreadia Service" },
  { value: "OLIVEEB_OVIZIA_SERVICE", label: "Oliveeb Ovizia Service" },
  { value: "OLIVEEB_OVACUS_SERVICE", label: "Oliveeb Ovacus Service" },
  { value: "OLIVEEB_ODDWIZE_SERVICE", label: "Oliveeb Oddwize Service" },
  { value: "anonymous-service", label: "Anonymous Service" },
];

export default function ActionableFormDrawer({
  open,
  onClose,
  actionable,
  onSubmit,
  userId,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // pre-fill form when editing
  useEffect(() => {
    if (actionable) {
      form.setFieldsValue({
        message: actionable.message,
        actionItemType: actionable.actionItemType,
        displayType: actionable.displayType,
        urgency: actionable.urgency,
        screen: actionable.screen,
        deepLink: actionable.deepLink,
        source: actionable.source || "OLITASK_MAIN_SERVICE",
        destination: actionable.destination || "OLITASK_MOBILE_UI",
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        source: "OLITASK_MAIN_SERVICE",
        destination: "OLITASK_MOBILE_UI",
      });
    }
  }, [actionable, form]);

  const handleFinish = async (values) => {
    if (!userId) {
      message.error("User ID is required");
      return;
    }

    setLoading(true);
    try {
      // backend payload
      const payload = {
        message: values.message,
        userId,
        actionItemId: userId,
        actionItemType: values.actionItemType,
        displayType: values.displayType,
        urgency: values.urgency,
        screen: values.screen || "",
        deepLink: values.deepLink || "",
        source: values.source,
        destination: values.destination,
      };

      // call parent onSubmit with payload
      await onSubmit(payload);

      // message.success(
      //   `Actionable ${actionable ? "updated" : "created"} successfully!`,
      // );
      form.resetFields();
      onClose();
    } catch (err) {
      message.error("Failed to submit actionable");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose?.();
  };

  return (
    <Drawer
      placement="right"
      size={isExpanded ? 520 : "100vw"}
      onClose={handleClose}
      open={open}
      style={{ fontFamily: "Inter, sans-serif" }}
      closable={false}
      zIndex={9999}
      footer={
        <div
          style={{
            textAlign: "right",
            maxWidth: isExpanded ? "100vw" : 700,
            margin: isExpanded ? 0 : "0 auto",
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <Button style={{ marginRight: 8 }} onClick={handleClose}>
            Close
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
          >
            {actionable ? "Update Actionable" : "Create Actionable"}
          </Button>
        </div>
      }
    >
      <div
        style={{
          maxWidth: isExpanded ? "100vw" : 700,
          margin: isExpanded ? 0 : "0 auto",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 15 }}>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {actionable ? "Edit Actionable" : "Create Actionable"}
            </span>
          </div>

          <ExpandOutlined
            onClick={() => setIsExpanded((prev) => !prev)}
            style={{ cursor: "pointer" }}
          />
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          style={{ fontFamily: "Inter", fontSize: 13 }}
        >
          <Form.Item
            label="Message"
            name="message"
            rules={[{ required: true, message: "Please enter a message" }]}
          >
            <TextArea rows={2} placeholder="Enter action message" />
          </Form.Item>

          <Form.Item
            label="Action Type"
            name="actionItemType"
            rules={[
              { required: true, message: "Please select an action type" },
            ]}
          >
            <Select
              placeholder="Select action type"
              options={ACTION_TYPE_OPTIONS}
              showSearch
              popupStyle={{ fontFamily: "Inter" }}
            />
          </Form.Item>

          <Form.Item
            label="Display Type"
            name="displayType"
            rules={[
              { required: true, message: "Please select a display type" },
            ]}
          >
            <Select
              placeholder="Select display type"
              options={displayOptions}
            />
          </Form.Item>

          <Form.Item
            label="Urgency"
            name="urgency"
            rules={[{ required: true, message: "Please select urgency" }]}
          >
            <Select placeholder="Select urgency" options={urgencyOptions} />
          </Form.Item>

          <Form.Item label="Screen" name="screen">
            <Input placeholder="Target screen" />
          </Form.Item>

          <Form.Item
            label="Deep Link"
            name="deepLink"
            rules={[{ required: true, message: "Please provide link" }]}
          >
            <Input placeholder="Deep link" />
          </Form.Item>

          <Form.Item
            label="Source"
            name="source"
            rules={[{ required: true, message: "Please select source" }]}
          >
            <Select placeholder="Select source" options={oliveebAppOptions} />
          </Form.Item>

          <Form.Item
            label="Destination"
            name="destination"
            rules={[{ required: true, message: "Please select destination" }]}
          >
            <Select
              placeholder="Select destination"
              options={oliveebAppOptions}
            />
          </Form.Item>
        </Form>
      </div>
    </Drawer>
  );
}
